// src/ui/ReplyComposer.jsx — the strip above the composer while you're writing a reply.
//
// The phone's ReplyPreviewCard: accent rail, "Replying to X" in the accent colour, a
// right-aligned TYPE badge, a 46px preview and a round ✕. It uses a slightly different
// palette from the in-bubble quote (audio #FB8C00 vs #FF9800, and so on); those small
// divergences are intentional in the app and are kept here so the two surfaces feel the
// same on both clients.
//
// The preview is the REAL media, not a glyph: the thumbnail already on the message doc
// for photos and video, the decrypted first page for a document, the live waveform for
// a voice note, the route for a flight, the options for a poll. That was the ask — you
// should be able to see WHAT you're replying to without scrolling back up to it.

import React from 'react';
import {
  Image as ImageIcon, Video, Mic, DocText, MapPin, Poll, Plane, Phone, Person, Smile, Play, X,
} from './icons';
import { replyKind, REPLY_LABEL, quoteTextFor, isRedacted } from '../services/replies';
import { docStyle, humanBytes } from '../services/documents';
import { timeLabel as voiceTimeLabel } from '../services/voice';

// ReplyPreviewCard.tsx:35-50 — the composer card's own accents
const CARD_ACCENT = {
  text: '#7B8794', image: '#5C6BC0', sticker: '#EC407A', video: '#EF5350', images: '#5C6BC0',
  audio: '#FB8C00', document: '#42A5F5', location: '#66BB6A', contact: '#AB47BC',
  poll: '#FFB300', flight: '#26C6DA', shared_post: '#7E57C2', link: '#29B6F6', call: '#4CAF50',
};
const KIND_ICON = {
  text: Smile, image: ImageIcon, sticker: Smile, video: Video, images: ImageIcon, audio: Mic,
  document: DocText, location: MapPin, contact: Person, poll: Poll, flight: Plane, call: Phone,
};

function subFor(kind, m) {
  switch (kind) {
    case 'audio': {
      const s = Number(m.duration);
      return Number.isFinite(s) && s > 0 ? voiceTimeLabel(s) : 'Voice message';
    }
    case 'document': return [docStyle(m.documentName || '').ext, humanBytes(m.documentSize)].filter(Boolean).join(' · ');
    case 'images': return `${(m.images || []).length} items`;
    case 'flight': {
      const f = m.flightData;
      return f?.origin?.code && f?.destination?.code ? `${f.origin.code} → ${f.destination.code}` : 'Flight';
    }
    case 'poll': return `${(m._payload?.options || []).length || 0} options`;
    case 'location': return m._payload?.name || 'Shared location';
    default: return '';
  }
}

export default function ReplyComposer({ message, name, onCancel }) {
  if (!message) return null;
  const kind = replyKind({ type: message.type, systemType: message.systemType, callData: message.callData }, message);
  // 🟥 a document's colour comes from its EXTENSION, not from the fact that it's a
  //    document — otherwise every file, PDF or spreadsheet or zip, is the same blue
  const docTint = kind === 'document' ? docStyle(message.documentName || '').color : null;
  const accent = docTint || CARD_ACCENT[kind] || CARD_ACCENT.text;
  const Icon = KIND_ICON[kind] || Smile;
  const label = REPLY_LABEL[kind];

  const b64 = message.imageThumbnailBase64 || message.videoThumbnailBase64
    || (Array.isArray(message.batchThumbnails) ? message.batchThumbnails.find(Boolean) : null);
  const thumb = b64 ? `data:image/jpeg;base64,${b64}`
    : (message._docThumb || (['image', 'sticker', 'gif'].includes(message.type) ? message._media : null));

  const title = kind === 'text'
    ? quoteTextFor(message)
    : (message.documentName || (message._caption || message.caption || '').trim() || REPLY_LABEL[kind] || 'Message');
  const sub = subFor(kind, message);

  return (
    <div className="rc" style={{ '--rc-accent': accent }}>
      <span className="rc-rail" />
      <div className="rc-wash">
        <div className="rc-head">
          <Icon width={13} height={13} />
          <span className="rc-to">Replying to {name}</span>
          {label && <span className="rc-badge">{label}</span>}
        </div>

        <div className="rc-body">
          {thumb ? (
            <span className="rc-thumb">
              <img src={thumb} alt="" className={kind === 'sticker' ? 'contain' : ''} />
              {(kind === 'video' || (kind === 'images' && message._mediaBatch?.some((x) => x?.kind === 'video')))
                && <span className="rc-play"><Play width={13} height={13} /></span>}
            </span>
          ) : kind !== 'text' ? (
            <span className={`rc-tile ${kind === 'document' ? 'doc' : ''}`}>
              <Icon width={22} height={22} />
            </span>
          ) : null}

          <span className="rc-txt">
            <span className={`rc-title ${kind === 'text' ? 'multi' : ''}`}>
              {isRedacted(title) ? (REPLY_LABEL[kind] || 'Message') : title}
            </span>
            {sub && <span className="rc-sub">{sub}</span>}
          </span>
        </div>
      </div>

      <button className="rc-x" onClick={onCancel} title="Cancel reply"><X width={15} height={15} /></button>
    </div>
  );
}
